/**
 * TASTEE — Welcome Screen (컨셉 H · 워드 마퀴)
 *
 * 구성
 *  - 배경: 아이보리(#f3efe6)
 *  - 중앙: "솔직 · 자유 · 비교" 키워드가 3열로 흐르는 타이포 마퀴
 *          (열마다 속도·방향·투명도 다르게 → 깊이감)
 *  - 하단: TASTEE 워드마크 + [시작하기] + [이미 계정이 있어요]
 *
 * 컬러 규칙
 *  - 아이보리 #f3efe6 / 브라운 #453d2c 두 가지만 사용
 *  - 브라운은 기본 80% 투명도, 워드마크·주요 버튼만 100%
 *
 * 폰트: Pretendard (전역 로드 가정. 아래 fallback 포함)
 * 접근성: prefers-reduced-motion 존중, 버튼 키보드 포커스 유지
 *
 * 의존성 없음(순수 React + CSS-in-JS). Flutter WebView 환경 호환.
 */

import React from "react";

const IVORY = "#FAF6EF";
const BROWN = "#4B3621";
/** 브라운 80% 불투명 (로고·주요 버튼 기본값) */
const BROWN_80 = "rgba(75, 54, 33, 0.8)";

interface WelcomeScreenProps {
  /** [시작하기] 탭 시 */
  onStart?: () => void;
  /** [다른 방법으로 계속하기] 탭 시 */
  onLogin?: () => void;
  /** 마퀴에 흐를 키워드 (기본: 솔직·자유·비교) */
  keywords?: string[];
}

export default function WelcomeScreen({
  onStart,
  onLogin,
  keywords = ["솔직", "자유", "비교"],
}: WelcomeScreenProps) {
  // 각 열의 시작 단어를 다르게 회전(rotate)시켜 세로로 각 잡히지 않고 섞이게
  // 1열: 솔직→  2열: 자유→  3열: 비교→
  const makeLine = (offset: number) => {
    const rotated = [...keywords.slice(offset), ...keywords.slice(0, offset)];
    return (rotated.join("  ·  ") + "  ·  ").repeat(6);
  };
  const lineA = makeLine(0); // 솔직 · 자유 · 비교 …
  const lineB = makeLine(1); // 자유 · 비교 · 솔직 …
  const lineC = makeLine(2); // 비교 · 솔직 · 자유 …

  // FOUT 방지: Pretendard(마퀴 Bold·버튼 SemiBold)가 로드된 뒤에만 마퀴를 노출한다.
  // font-display:swap 때문에 폴백폰트로 먼저 그렸다가 교체되면 글자폭이 바뀌어
  // width:max-content 기준 -50% 위치가 어긋나며 단어가 튀는(리플로우) 현상 제거.
  const [ready, setReady] = React.useState(false);
  React.useEffect(() => {
    let done = false;
    const reveal = () => {
      if (!done) {
        done = true;
        setReady(true);
      }
    };
    const fonts = (document as any).fonts;
    if (fonts && fonts.load) {
      Promise.all([
        fonts.load('700 48px Pretendard'),
        fonts.load('600 16px Pretendard'),
      ])
        .then(reveal)
        .catch(reveal);
    } else {
      reveal();
    }
    // 폰트 로드가 지나치게 느릴 때의 안전장치(최대 1.5s 후 그냥 노출)
    const t = setTimeout(reveal, 1500);
    return () => {
      done = true;
      clearTimeout(t);
    };
  }, []);

  return (
    <div style={styles.root}>
      <style>{css}</style>

      {/* 스크린리더용 대체 텍스트 (폰트 로드와 무관하게 항상) */}
      <p style={styles.srOnly}>
        TASTEE. 솔직하고 자유롭게 비교하는 친구 기반 맛집 기록 앱입니다.
      </p>

      {/* 폰트 로드 완료 후에만 노출 → 폴백→Pretendard 스왑 리플로우가 화면에서 안 보임 */}
      {ready && (
        <>
          {/* 중앙 마퀴 3열 */}
          <main style={styles.stage} aria-hidden="true">
            <div className="ts-row ts-row--a">
              <span>{lineA}</span>
              <span>{lineA}</span>
            </div>
            <div className="ts-row ts-row--b">
              <span>{lineB}</span>
              <span>{lineB}</span>
            </div>
            <div className="ts-row ts-row--c">
              <span>{lineC}</span>
              <span>{lineC}</span>
            </div>
          </main>

          {/* 하단 CTA */}
          <footer style={styles.foot}>
            <button
              type="button"
              className="ts-btn ts-btn--primary"
              onClick={onStart}
            >
              시작하기
            </button>

            <button
              type="button"
              className="ts-btn ts-btn--ghost"
              onClick={onLogin}
            >
              다른 방법으로 계속하기
            </button>
          </footer>
        </>
      )}
    </div>
  );
}

/* ---------- 인라인 스타일 (레이아웃) ---------- */
const styles: Record<string, React.CSSProperties> = {
  root: {
    position: "relative",
    width: "100%",
    height: "100dvh",
    minHeight: "100%",
    background: IVORY,
    fontFamily:
      'Pretendard, -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo", sans-serif',
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    WebkitTapHighlightColor: "transparent",
  },
  stage: {
    position: "relative",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "clamp(4px, 2vh, 18px)",
    overflow: "hidden",
    // 진입 시 마퀴가 '빡' 튀지 않고 부드럽게 페이드인(배경 크림은 고정, 콘텐츠만)
    animation: "ts-appear 0.6s ease both",
    // 좌우 페이드로 흐름이 화면 밖에서 시작/끝나는 느낌
    WebkitMaskImage:
      "linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)",
    maskImage:
      "linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)",
  },
  foot: {
    position: "relative",
    zIndex: 2,
    padding: "0 24px",
    paddingBottom: "calc(24px + env(safe-area-inset-bottom))",
    textAlign: "center",
  },
  srOnly: {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
    border: 0,
  },
};

/* ---------- CSS (애니메이션 · 버튼 · 마퀴) ---------- */
const css = `
.ts-row{
  display:flex;
  width:max-content;               /* 세로 flex 안에서 화면폭으로 stretch 되는 것 방지 →
                                      -50%가 '한 벌 폭'과 정확히 일치해 seamless 무한 루프 */
  white-space:nowrap;
  will-change:transform;
  font-weight:700;
  line-height:1.05;
  color:${BROWN};
}
.ts-row span{ padding-right:0; }

/* 3열: 크기는 동일, 시작 단어·속도·방향을 달리해 섞이며 흐름 */
/* 가운데(b열)가 가장 진해 앞으로 나와 보이도록 */
.ts-row{ font-size:clamp(34px, 12vw, 56px); }
.ts-row--a{
  opacity:0.34;                     /* 중간 */
  animation:ts-marq-left 200s linear infinite;
}
.ts-row--b{
  opacity:0.80;                     /* 앞쪽(브라운 기본 80%) */
  animation:ts-marq-right 148s linear infinite;
}
.ts-row--c{
  opacity:0.16;                     /* 뒤쪽(옅게) */
  animation:ts-marq-left 264s linear infinite;
}

@keyframes ts-marq-left{
  from{ transform:translate3d(0,0,0); }
  to{   transform:translate3d(-50%,0,0); }
}
@keyframes ts-marq-right{
  from{ transform:translate3d(-50%,0,0); }
  to{   transform:translate3d(0,0,0); }
}

/* 버튼 */
.ts-btn{
  display:block;
  width:100%;
  border:none;
  border-radius:16px;
  font-family:inherit;
  font-size:16px;
  font-weight:600;
  cursor:pointer;
  transition:transform .12s ease, opacity .12s ease;
  -webkit-tap-highlight-color:transparent;
}
.ts-btn:active{ transform:scale(0.985); }
.ts-btn:focus-visible{
  outline:2px solid ${BROWN};
  outline-offset:3px;
}

.ts-btn--primary{
  background:${BROWN_80};           /* 주요 버튼 80% 불투명 */
  color:${IVORY};
  border-radius:30px;              /* corner radius 30 */
  padding:16px;
  margin-bottom:10px;
}
.ts-btn--ghost{
  background:transparent;
  color:${BROWN_80};                /* #4B3621 80% */
  font-size:16px;
  font-weight:700;                  /* bold */
  padding:10px;
}
.ts-btn--ghost:active{ opacity:0.7; }

/* 진입 시 워드마크·버튼 살짝 상승 */
.ts-btn--primary,
.ts-btn--ghost{ animation:ts-rise .5s cubic-bezier(.2,.8,.2,1) backwards; }
.ts-btn--primary{ animation-delay:.15s; }
.ts-btn--ghost{ animation-delay:.25s; }
@keyframes ts-rise{
  from{ opacity:0; transform:translateY(10px); }
  to{ opacity:1; transform:translateY(0); }
}

/* 진입 페이드인(하드 컷 '빡' 방지) */
@keyframes ts-appear{
  from{ opacity:0; }
  to{ opacity:1; }
}

/* 모션 최소화 선호 시: 흐름 정지, 정적 배치 */
@media (prefers-reduced-motion: reduce){
  .ts-row{ animation:none !important; }
  .ts-row--a{ transform:translate3d(-8%,0,0); }
  .ts-row--b{ transform:translate3d(-24%,0,0); }
  .ts-row--c{ transform:translate3d(-14%,0,0); }
  .ts-btn--primary,.ts-btn--ghost{ animation:none; }
}
`;
